import { createSlice } from "@reduxjs/toolkit";
import { isVoid } from "@/shared/utils";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { NotesManagerSlice, NotesTableItem } from "./types";

const initialState: NotesManagerSlice = {
  notes: [],
  selectedNotes: [],
  activeNote: null,
};

export const notesManagerSlice = createSlice({
  name: "notesManager",
  initialState,
  reducers: {
    openNoteById(state, action: PayloadAction<{ noteId: string }>) {
      const newNote = state.notes.find((note) => note.id === action.payload.noteId);

      if (newNote) {
        if (!state.selectedNotes.some((note) => note.id === newNote.id)) {
          state.selectedNotes = [...state.selectedNotes, newNote];
        }
        state.activeNote = newNote;
      }
    },
    setNotes(state, action: PayloadAction<NotesTableItem[]>) {
      state.notes = action.payload;

      if (isVoid(state.selectedNotes)) {
        state.selectedNotes = [action.payload[0]];
        state.activeNote = action.payload[0];
      }
    },
    setActiveNote(state, action: PayloadAction<{ noteId: string }>) {
      const { noteId } = action.payload;

      state.activeNote = state.selectedNotes.find((note) => note.id === noteId) ?? null;
    },
    updateActiveNote(state, action: PayloadAction<NotesTableItem>) {
      const updatedNote = action.payload;

      const noteIndex = state.selectedNotes.findIndex((note) => note.id === updatedNote.id);

      if (noteIndex !== -1) {
        state.selectedNotes[noteIndex] = updatedNote;
      }

      state.activeNote = updatedNote;
    },
    updateDraftNoteId(state, action: PayloadAction<{ tempId: string; newId: string }>) {
      const { tempId, newId } = action.payload;

      const noteIndex = state.selectedNotes.findIndex((note) => note.id === tempId);

      if (noteIndex !== -1) {
        state.selectedNotes[noteIndex].id = newId;
      }

      if (state.activeNote?.id === tempId) {
        state.activeNote.id = newId;
      }
    },
    addSelectedNote(state, action: PayloadAction<NotesTableItem>) {
      if (state.selectedNotes.some((note) => note.id === action.payload.id)) {
        return;
      }
      state.activeNote = action.payload;
      state.selectedNotes = [...state.selectedNotes, action.payload];
    },
    setSelectedNotes(state, action: PayloadAction<NotesTableItem[]>) {
      state.selectedNotes = action.payload;
    },
    removeSelectedNote(state, action: PayloadAction<{ noteId: string }>) {
      const { noteId } = action.payload;

      const newNotes = state.selectedNotes.filter((note) => note.id !== noteId);

      if (state.activeNote?.id === noteId && newNotes.length > 0) {
        state.activeNote = newNotes[0];
      }
      state.selectedNotes = newNotes;
    },
  },
  selectors: {
    getAllNotes: (state: NotesManagerSlice) => state.notes,
    getSelectedNotes: (state: NotesManagerSlice) => state.selectedNotes,
    getActiveNote: (state: NotesManagerSlice) => state.activeNote,
  },
});

export const { actions: notesManagerActions, selectors: notesManagerSelectors } = notesManagerSlice;
